import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Search, Filter, Plus, ChevronDown, ChevronUp, 
  Eye, Edit, Trash2, MoreVertical 
} from 'lucide-react';

const ViewMember = () => {
  const navigate = useNavigate();
  const [members, setMembers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const itemsPerPage = 10;

  // Updated Mock Data with Ethiopian/African Context Photos
  const sampleMembers = [
    {
      id: 'CU/2024/1001',
      fullName: 'Reshid Musxafaa',
      age: 28,
      FIN: '8464-3774-8284',
      photo: 'https://images.unsplash.com', // Ethiopian/African man
      email: 'reshid.musxafaa@example.com',
      phone: '+251 912 345 001',
      gender: 'male',
      joinDate: '2024-01-15',
      shareholderLevel: 'silver',
      status: 'active',
      occupation: 'Farmer'
    },
    {
      id: 'CU/2024/1004',
      fullName: 'Aisha Mohamed',
      age: 34,
      FIN: '5566-2211-3344',
      photo: 'https://images.unsplash.com', // African woman
      email: 'aisha.mohamed@example.com',
      phone: '+251 912 345 004',
      gender: 'female',
      joinDate: '2024-03-01',
      shareholderLevel: 'gold',
      status: 'active',
      occupation: 'Nurse'
    },
    {
      id: 'CU/2024/1005',
      fullName: 'Bekele Tadesse',
      age: 52,
      FIN: '7788-9900-1122',
      photo: 'https://images.unsplash.com',
      email: 'bekele.tadesse@example.com',
      phone: '+251 912 345 005',
      gender: 'male',
      joinDate: '2024-03-12',
      shareholderLevel: 'silver',
      status: 'inactive',
      occupation: 'Driver'
    },
    {
      id: 'CU/2024/1006',
      fullName: 'Selam Haile',
      age: 22,
      FIN: '3344-5566-7788',
      photo: 'https://images.unsplash.com',
      email: 'selam.haile@example.com',
      phone: '+251 912 345 006',
      gender: 'female',
      joinDate: '2024-04-02',
      shareholderLevel: 'gold',
      status: 'active',
      occupation: 'Teacher'
    },
    {
      id: 'CU/2024/1007',
      fullName: 'Mekonnen Alemu',
      age: 45,
      FIN: '9900-1122-3344',
      photo: 'https://images.unsplash.com',
      email: 'mekonnen.alemu@example.com',
      phone: '+251 912 345 007',
      gender: 'male',
      joinDate: '2024-04-15',
      shareholderLevel: 'bronze',
      status: 'active',
      occupation: 'Shopkeeper'
    },
    {
      id: 'CU/2024/1008',
      fullName: 'Fatuma Yusuf',
      age: 31,
      FIN: '2233-4455-6677',
      photo: 'https://images.unsplash.com',
      email: 'fatuma.yusuf@example.com',
      phone: '+251 912 345 008',
      gender: 'female',
      joinDate: '2024-05-01',
      shareholderLevel: 'silver',
      status: 'active',
      occupation: 'Engineer'
    },
    {
      id: 'CU/2024/1009',
      fullName: 'Tesfaye Bekele',
      age: 39,
      FIN: '8877-6655-4433',
      photo: 'https://images.unsplash.com',
      email: 'tesfaye.bekele@example.com',
      phone: '+251 912 345 009',
      gender: 'male',
      joinDate: '2024-05-10',
      shareholderLevel: 'gold',
      status: 'active',
      occupation: 'Engineer'
    },
    {
      id: 'CU/2024/1010',
      fullName: 'Halima Abdi',
      age: 27,
      FIN: '5566-7788-9900',
      photo: 'https://images.unsplash.com',
      email: 'halima.abdi@example.com',
      phone: '+251 912 345 010',
      gender: 'female',
      joinDate: '2024-05-20',
      shareholderLevel: 'bronze',
      status: 'inactive',
      occupation: 'Artist'
    },
  ];

  useEffect(() => {
    const timer = setTimeout(() => {
      setMembers(sampleMembers);
      setLoading(false);
    }, 800);
    return () => clearTimeout(timer);
  }, []);

  const filteredMembers = members.filter(m => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return true;
    return (
      (m.fullName && m.fullName.toLowerCase().includes(q)) ||
      (m.id && m.id.toLowerCase().includes(q)) ||
      (m.FIN && m.FIN.toLowerCase().includes(q))
    );
  });

  const stats = React.useMemo(() => {
    const gender = { male: 0, female: 0, other: 0 };
    const levels = { gold: 0, silver: 0, bronze: 0 };
    const ageGroups = { '<30': 0, '30-50': 0, '>50': 0 };
    members.forEach(m => {
      if (gender[m.gender] !== undefined) gender[m.gender]++;
      else gender.other++;
      if (levels[m.shareholderLevel] !== undefined) levels[m.shareholderLevel]++;
      else levels.bronze++;
      const age = m.age || 0;
      if (age < 30) ageGroups['<30']++;
      else if (age <= 50) ageGroups['30-50']++;
      else ageGroups['>50']++;
    });
    return { gender, levels, ageGroups };
  }, [members]);

  const paginatedMembers = filteredMembers.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">C-ONE Members</h1>
            <p className="text-gray-500 text-sm">Managing {members.length} total members</p>
          </div>
          <button 
            onClick={() => navigate('/addmember')}
            className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-5 py-2.5 rounded-lg transition-colors font-medium"
          >
            <Plus size={20} />
            Add Member
          </button>
        </div>

        {/* Statistics */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4 mb-6">
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">Male</h3>
            <p className="text-2xl font-bold text-gray-900">{stats.gender.male}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">Female</h3>
            <p className="text-2xl font-bold text-gray-900">{stats.gender.female}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">Gold Members</h3>
            <p className="text-2xl font-bold text-gray-900">{stats.levels.gold}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">Other Levels</h3>
            <p className="text-2xl font-bold text-gray-900">{stats.levels.silver + stats.levels.bronze}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">Age &lt;30</h3>
            <p className="text-2xl font-bold text-gray-900">{stats.ageGroups['<30']}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">Age 30–50</h3>
            <p className="text-2xl font-bold text-gray-900">{stats.ageGroups['30-50']}</p>
          </div>
        </div>

        {/* Search Bar */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search by name or ID..."
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/20 focus:border-green-500 transition-all"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {/* Members Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Me</th>
                  <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Member ID</th>
                  <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">FIN</th>
                  <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Occupation</th>
                  <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Level</th>
                  <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {loading ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-10 text-center text-gray-400">Loading data...</td>
                  </tr>
                ) : paginatedMembers.length > 0 ? (
                  paginatedMembers.map((member) => (
                    <tr key={member.id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <img 
                            src={member.photo} 
                            alt={member.fullName} 
                            className="w-10 h-10 rounded-full object-cover border border-gray-200"
                          />
                          <div>
                            <div className="font-semibold text-gray-900">{member.fullName}</div>
                            <div className="text-xs text-gray-500">{member.phone}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600 font-mono">{member.id}</td>
                      <td className="px-6 py-4 text-sm text-gray-600 font-mono">{member.FIN}</td>
                      <td className="px-6 py-4 text-sm text-gray-600">{member.occupation}</td>
                      <td className="px-6 py-4">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-medium capitalize 
                          ${member.shareholderLevel === 'gold' ? 'bg-amber-100 text-amber-700' : 
                            member.shareholderLevel === 'silver' ? 'bg-slate-100 text-slate-700' : 
                            'bg-blue-100 text-blue-700'}`}>
                          {member.shareholderLevel}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium
                          ${member.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${member.status === 'active' ? 'bg-green-600' : 'bg-red-600'}`}></span>
                          {member.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex justify-end gap-2">
                          <button className="p-1.5 hover:bg-gray-100 rounded-md text-gray-500 transition-colors"><Eye size={18}/></button>
                          <button className="p-1.5 hover:bg-gray-100 rounded-md text-blue-500 transition-colors"><Edit size={18}/></button>
                          <button className="p-1.5 hover:bg-gray-100 rounded-md text-red-500 transition-colors"><Trash2 size={18}/></button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="px-6 py-10 text-center text-gray-400">No members found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewMember;
